import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BookOpen, Users, Calendar, FileText, Award, MessageSquare, ChevronRight } from 'lucide-react'

export const Route = createFileRoute('/feature/teacher')({
  component: TeacherFeaturesPage,
})

interface Feature {
  id: number
  icon: JSX.Element
  title: string
  summary: string
  keyPoints: string[]
}

function TeacherFeaturesPage() {
  const { t } = useTranslation('features')
  const mainColor = '#525FE1'

  const features: Feature[] = [
    {
      id: 1,
      icon: <BookOpen className="w-8 h-8" />,
      title: t('teacher.lessonPlanning.title'),
      summary: t('teacher.lessonPlanning.description'),
      keyPoints: ['Guided lesson planning', 'Resource integration', 'Collaborative planning']
    },
    {
      id: 2,
      icon: <Users className="w-8 h-8" />,
      title: t('teacher.studentManagement.title'),
      summary: t('teacher.studentManagement.description'),
      keyPoints: ['Student progress tracking', 'Class roster management', 'Performance records']
    },
    {
      id: 3,
      icon: <Calendar className="w-8 h-8" />,
      title: t('teacher.attendance.title'),
      summary: t('teacher.attendance.description'),
      keyPoints: ['Digital attendance', 'Automated notifications', 'Detailed reports']
    },
    {
      id: 4,
      icon: <FileText className="w-8 h-8" />,
      title: t('teacher.assessments.title'),
      summary: t('teacher.assessments.description'),
      keyPoints: ['Auto-grading', 'Multiple question types', 'Comprehensive feedback']
    },
    {
      id: 5,
      icon: <Award className="w-8 h-8" />,
      title: t('teacher.grading.title'),
      summary: t('teacher.grading.description'),
      keyPoints: ['Organized gradebook', 'Weighted categories', 'Progress tracking']
    },
    {
      id: 6,
      icon: <MessageSquare className="w-8 h-8" />,
      title: t('teacher.communication.title'),
      summary: t('teacher.communication.description'),
      keyPoints: ['Direct messaging', 'Announcements', 'Discussion forums']
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="mb-12">
          <a
            href="/features"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToFeatures')}
          </a>
          
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm" style={{ backgroundColor: mainColor }}>
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('teacher.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('teacher.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature) => (
            <div 
              key={feature.id} 
              className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col"
            >
              <div 
                className="px-6 py-5"
                style={{ backgroundColor: mainColor }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white leading-tight">
                    {feature.title}
                  </h3>
                </div>
              </div>
              
              <div className="px-6 py-5 flex flex-col flex-grow">
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  {feature.summary}
                </p>
                
                <div className="mb-5 flex-grow">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Key Points
                  </h4>
                  <ul className="space-y-2.5">
                    {feature.keyPoints.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start text-sm text-gray-600">
                        <span 
                          className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                          style={{ backgroundColor: mainColor }}
                        ></span>
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div
                  className="mt-auto flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 hover:shadow-sm text-sm font-medium cursor-not-allowed opacity-60"
                  style={{ 
                    backgroundColor: '#f8f9ff', 
                    borderColor: `${mainColor}30`,
                    color: mainColor
                  }}
                >
                  <span>Learn More</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-10 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('teacher.benefits.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('teacher.benefits.productivity.title')}
              </h3>
              <ul className="space-y-2.5 text-gray-600">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.productivity.item1')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.productivity.item2')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.productivity.item3')}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('teacher.benefits.engagement.title')}
              </h3>
              <ul className="space-y-2.5 text-gray-600">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.engagement.item1')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.engagement.item2')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('teacher.benefits.engagement.item3')}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {t('teacher.cta.title')}
          </h3>
          <p className="text-gray-600 mb-8">
            {t('teacher.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register"
              className="text-white px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90"
              style={{ backgroundColor: mainColor }}
            >
              {t('teacher.cta.getStarted')}
            </a>
            <a
              href="/demo"
              className="border px-6 py-3 rounded-lg font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: mainColor, color: mainColor }}
            >
              {t('teacher.cta.scheduleDemo')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
